import { capitalize, startCase } from 'lodash-es';
import { filter, fromEvent, map, switchMap, takeUntil, tap } from 'rxjs';

import { useSubscriptions } from '@/hook';
import { withTarget } from '@/util';

import { VFlex } from '../VFlex';
import { VGrid } from '../VGrid';
import { useVTheme } from '../VThemeProvider';
import { joinCn } from '../util';
import { useVBaseTableContext } from './hook';
import style from './style';

const VInnerBaseTableThead = () => {
  const {
    colsWidth,
    columnList,
    columnRenderer,
    columnStyleMap,
    hasScroll,
    visibleTheadShadow,
    resizable = false,
    $thead,
    handleColumnResize,
    rowSize,
  } = useVBaseTableContext();
  const { themeName } = useVTheme();
  const twStyle = style({
    visibleShadow: visibleTheadShadow,
    rowSize,
    themeName,
  });

  useSubscriptions(
    () => [
      fromEvent<MouseEvent>(window, 'mousedown')
        .pipe(
          filter(() => !!$thead.current),
          withTarget,
          filter(
            e =>
              e.target.dataset.testid === 'v-base-table-resize-handle' &&
              $thead.current!.contains(e.target),
          ),
          map(e => ({
            target: e.target.closest(
              '[data-testid="v-inner-base-table-th"]',
            ) as HTMLDivElement,
            startX: e.clientX,
          })),
          switchMap(({ target, startX }) => {
            return fromEvent<MouseEvent>(window, 'mousemove').pipe(
              map(e => ({ endX: e.clientX })),
              tap(({ endX }) => {
                const newWidth = target.offsetWidth + (endX - startX);

                handleColumnResize(
                  target.dataset.column!,
                  Math.max(newWidth, 80),
                );

                startX = endX;
              }),
              takeUntil(fromEvent(window, 'mouseup')),
              takeUntil(fromEvent(window, 'blur')),
            );
          }),
        )
        .subscribe(),
    ],
    [],
  );

  return (
    <VFlex
      role="rowheader"
      ref={$thead}
      className={joinCn(twStyle.vBaseTableTheadTr)}
      data-testid="v-inner-base-thead-tr"
    >
      <VGrid className="flex-1" cols={columnList.length} colsWidth={colsWidth}>
        {columnList.map(col => {
          return (
            <div
              key={col}
              role="columnheader"
              className={joinCn(
                twStyle.vBaseTableTh,
                columnStyleMap[col]?.className,
                resizable && 'relative',
              )}
              style={columnStyleMap[col]?.style}
              data-column={col}
              data-testid="v-inner-base-table-th"
            >
              {typeof columnRenderer?.[col] === 'function'
                ? columnRenderer?.[col](col)
                : capitalize(startCase(col))}
              {resizable && (
                <div
                  className={twStyle.vBaseTableResizeHandle}
                  data-testid="v-base-table-resize-handle"
                />
              )}
            </div>
          );
        })}
      </VGrid>
      {hasScroll && <div className="w-2.5">&nbsp;</div>}
    </VFlex>
  );
};

VInnerBaseTableThead.displayName = 'VInnerBaseTableThead';

export default VInnerBaseTableThead;
